import { CSSProperties } from 'react';
import { ImageSection as ImageSectionType } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/composer.pb';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button } from '@frontend/design-system';
import { useComposerSection } from '../../../hooks';
import { Corners, FrameAlignment } from '../../../types';
import { getCornersStyle } from '../../../utils';
import { FloatingButton } from '../floating-toolbar';
import { EmailTable } from './email-table';
import { ImageWithDimensions } from './types';
import {
  calculateHorizontalJustifiedLayout,
  EMPTY_IMAGE_SIZE,
  getAlignment,
  getCorners,
  getJustifiedLayoutPaddingStyle,
  getNonNegativeNumber,
} from './utils';

type JustifiedLayoutProps = {
  alignment?: string;
  corners?: string;
  height?: string;
  imageSpacing?: number;
  images: ImageWithDimensions[];
  onLoad?: (imageIndex: number, width: number, height: number) => void;
  onRemove?: (imageIndex: number) => void;
  onReplace?: (imageIndex: number) => void;
  onSelect?: (imageIndex?: number) => void;
  selectedImageIndex?: number;
};

export const JustifiedLayout = ({
  height: passedHeight,
  alignment: passedAlignment,
  corners: passedCorners,
  imageSpacing: passedImageSpacing,
  ...props
}: JustifiedLayoutProps) => {
  const alignment = getAlignment(passedAlignment);
  const corners = getCorners(passedCorners);
  const height = getNonNegativeNumber(passedHeight);
  const imageSpacing = getNonNegativeNumber(passedImageSpacing);

  return height !== undefined ? (
    <HorizontalJustifiedLayout
      alignment={alignment}
      corners={corners}
      height={height}
      imageSpacing={imageSpacing}
      {...props}
    />
  ) : (
    <VerticalJustifiedLayout alignment={alignment} corners={corners} imageSpacing={imageSpacing} {...props} />
  );
};

type TypedJustifiedLayoutProps = Omit<JustifiedLayoutProps, 'alignment' | 'corners' | 'height' | 'imageSpacing'> & {
  alignment: FrameAlignment;
  corners: Corners;
  height?: number;
  imageSpacing?: number;
};

type HorizontalJustifiedLayoutProps = Omit<TypedJustifiedLayoutProps, 'height'> & {
  height: number;
};

const HorizontalJustifiedLayout = ({
  alignment,
  corners,
  height,
  imageSpacing,
  images,
  onLoad,
  onRemove,
  onReplace,
  onSelect,
  selectedImageIndex,
}: HorizontalJustifiedLayoutProps) => {
  const { isSelected: isSectionSelected } = useComposerSection<ImageSectionType>();
  const { percentageWidths, percentageSpacing, maxTableWidth } = calculateHorizontalJustifiedLayout(
    images,
    height,
    imageSpacing
  );
  const isEmpty = images.length === 0 || images.every((image) => !image.url);

  return (
    <EmailTable
      alignment={alignment}
      style={{
        width: '100%',
        maxWidth: maxTableWidth,
        height: isEmpty && onSelect ? `${EMPTY_IMAGE_SIZE}px` : undefined,
      }}
    >
      <EmailTable.Row>
        {percentageWidths.map((percentageWidth, index) => (
          <EmailTable.Cell
            align={alignment}
            key={index}
            style={{
              ...getJustifiedLayoutPaddingStyle(index, images.length, percentageSpacing),
              width: percentageWidth,
              position: 'relative',
              height: onSelect && !images[index]?.url ? 0 : undefined, // Makes the cell "growable" so the Add Image tile looks decent
              textAlign: alignment,
            }}
            onClick={onSelect ? (e) => e.stopPropagation() : undefined}
          >
            <JustifiedImage
              src={images[index]?.url}
              alt={images[index]?.name}
              style={{
                // TODO: The corner styling needs to be adjusted for the table padding that's used to space things out
                ...getCornersStyle(corners),
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
              isSelected={isSectionSelected && selectedImageIndex === index}
              onLoad={onLoad ? (width, height) => onLoad(index, width, height) : undefined}
              onRemove={onRemove ? () => onRemove(index) : undefined}
              onReplace={onReplace ? () => onReplace(index) : undefined}
              onSelect={onSelect ? () => onSelect(index) : undefined}
            />
          </EmailTable.Cell>
        ))}
      </EmailTable.Row>
    </EmailTable>
  );
};

type VerticalJustifiedLayoutProps = Omit<TypedJustifiedLayoutProps, 'height'>;

const VerticalJustifiedLayout = ({
  alignment,
  corners,
  imageSpacing,
  images,
  onLoad,
  onRemove,
  onReplace,
  onSelect,
  selectedImageIndex,
}: VerticalJustifiedLayoutProps) => {
  const { isSelected: isSectionSelected } = useComposerSection<ImageSectionType>();
  const isInteractive = !!onLoad || !!onRemove || !!onReplace || !!onSelect;

  return images.length ? (
    <EmailTable alignment={alignment} style={{ width: '100%', maxWidth: '100%' }}>
      <EmailTable.Row>
        <EmailTable.Cell alignment={alignment}>
          {images.map((image, index) => (
            <OptionalContainer
              key={index}
              render={isInteractive}
              style={{
                position: 'relative',
                height: !image.url ? `${EMPTY_IMAGE_SIZE}px` : undefined, // Creates the "tile" for the Add Image button
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <JustifiedImage
                src={image.url}
                alt={image.name}
                style={{
                  ...getJustifiedLayoutPaddingStyle(index, images.length, imageSpacing, 'px', true),
                  // TODO: The corner styling needs to be adjusted for the table padding that's used to space things out
                  ...getCornersStyle(corners),
                  width: '100%',
                  maxWidth: image.width + 'px',
                  height: 'auto',
                  display: 'block',
                  margin: '0 auto',
                }}
                isSelected={isSectionSelected && selectedImageIndex === index}
                onLoad={onLoad ? (width, height) => onLoad(index, width, height) : undefined}
                onRemove={onRemove ? () => onRemove(index) : undefined}
                onReplace={onReplace ? () => onReplace(index) : undefined}
                onSelect={onSelect ? () => onSelect(index) : undefined}
              />
            </OptionalContainer>
          ))}
        </EmailTable.Cell>
      </EmailTable.Row>
    </EmailTable>
  ) : null;
};

type OptionalContainerProps = React.HTMLAttributes<HTMLDivElement> & {
  render: boolean;
};

const OptionalContainer = ({ render, children, ...props }: OptionalContainerProps) =>
  render ? <div {...props}>{children}</div> : <>{children}</>;

type JustifiedImageProps = {
  alt?: string;
  isSelected?: boolean;
  onLoad?: (width: number, height: number) => void;
  onRemove?: () => void;
  onReplace?: () => void;
  onSelect?: () => void;
  src?: string;
  style?: CSSProperties;
};

const JustifiedImage = ({
  alt,
  isSelected,
  onLoad,
  onRemove,
  onReplace,
  onSelect,
  src,
  style,
}: JustifiedImageProps) => {
  const { t } = useTranslation('content-composer');

  // TODO: Handle load failures. This means the image has failed to load:
  // img.complete === true && img.naturalWidth === 0 && img.naturalHeight === 0
  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    onLoad?.(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight);
  };

  const image = (
    <img
      src={src}
      alt={alt}
      style={style}
      data-type='src-image'
      onClick={onSelect}
      onLoad={onLoad ? handleLoad : undefined}
    />
  );

  if (!onSelect) return src ? image : null;

  return src ? (
    <>
      {image}
      {isSelected && (
        <div
          css={{
            display: 'flex',
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translate(-50%, 100%)',
            zIndex: theme.zIndex.low,
            columnGap: theme.spacing(1),
          }}
        >
          {onReplace && <FloatingButton onClick={onReplace}>{t('Replace')}</FloatingButton>}
          {onRemove && <FloatingButton onClick={onRemove}>{t('Remove')}</FloatingButton>}
        </div>
      )}
    </>
  ) : (
    <div
      style={{
        // The justified layout uses padding for margin which gets weird when applied to the Add Image button tile.
        // Converting it to margin makes it look acceptable.
        margin: style?.padding,
        marginTop: style?.paddingTop,
        marginRight: style?.paddingRight,
        marginBottom: style?.paddingBottom,
        marginLeft: style?.paddingLeft,
        // End padding-to-margin conversion

        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: `solid 1px ${theme.colors.neutral20}`,
        height: '100%',
        padding: theme.spacing(1),
      }}
    >
      <Button onClick={onReplace} variant='secondary' size='small' iconName='plus-small'>
        {t('Add Image')}
      </Button>
    </div>
  );
};
